'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  QR_ECC_OPTIONS,
  encodeQr,
  qrToSvg,
  verifyQrEncoder,
  type QrEcc,
} from '@/lib/browser-qr-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const SIZE_OPTIONS = [
  { value: '256', label: 'Small (256 px)' },
  { value: '512', label: 'Medium (512 px)' },
  { value: '1024', label: 'Large (1024 px)' },
];

// Content types. Keep this list and the payload rules below in sync with the Cleanor QR
// Chrome extension so a code made here scans identically to one made there.
type QrType =
  | 'text'
  | 'wifi'
  | 'email'
  | 'phone'
  | 'sms'
  | 'contact'
  | 'whatsapp'
  | 'geo'
  | 'event';

const TYPE_OPTIONS: Array<{ value: QrType; label: string }> = [
  { value: 'text', label: 'Text / Link' },
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'sms', label: 'SMS' },
  { value: 'contact', label: 'Contact (vCard)' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'geo', label: 'Location' },
  { value: 'event', label: 'Event' },
];
const TYPE_SET = new Set<QrType>(TYPE_OPTIONS.map((option) => option.value));

type Fields = Record<string, string>;
const INITIAL_FIELDS: Fields = { text: 'https://cleanor.app', enc: 'WPA' };

// ---- payload builders (ported from the extension) ---------------------------
const escWifi = (s: string) => s.replace(/([\\;,:"])/g, '\\$1');
const escVcard = (s: string) => s.replace(/([\\;,])/g, '\\$1');
const escICal = (s: string) => s.replace(/([\\;,])/g, '\\$1').replace(/\r?\n/g, '\\n');

function icalDate(v: string): string {
  const m = v.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? `${m[1]}${m[2]}${m[3]}T${m[4]}${m[5]}00` : '';
}
function icalDateOnly(v: string): string {
  const m = v.match(/(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}${m[2]}${m[3]}` : '';
}
const pad2 = (n: number) => String(n).padStart(2, '0');
function nextDay(ymd: string): string {
  const d = new Date(+ymd.slice(0, 4), +ymd.slice(4, 6) - 1, +ymd.slice(6, 8));
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}
function plusOneHour(dtLocal: string): string {
  const m = dtLocal.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return '';
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  d.setHours(d.getHours() + 1);
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}T${pad2(d.getHours())}${pad2(d.getMinutes())}00`;
}

function buildPayload(type: QrType, f: Fields, allDay: boolean): string {
  const g = (k: string) => (f[k] ?? '').trim();
  switch (type) {
    case 'text':
      return g('text');
    case 'wifi': {
      const ssid = g('ssid');
      if (!ssid) return '';
      const enc = f.enc || 'WPA';
      if (enc === 'nopass') return `WIFI:T:nopass;S:${escWifi(ssid)};;`;
      return `WIFI:T:${enc};S:${escWifi(ssid)};P:${escWifi(f.pass ?? '')};;`;
    }
    case 'email': {
      const to = g('emailTo');
      if (!to) return '';
      const q = new URLSearchParams();
      if (g('emailSubject')) q.set('subject', g('emailSubject'));
      if (g('emailBody')) q.set('body', g('emailBody'));
      const qs = q.toString();
      return `mailto:${to}${qs ? '?' + qs : ''}`;
    }
    case 'phone': {
      const n = g('phone');
      return n ? `tel:${n.replace(/\s+/g, '')}` : '';
    }
    case 'sms': {
      const n = g('smsNumber');
      if (!n) return '';
      return `SMSTO:${n.replace(/\s+/g, '')}:${g('smsMessage')}`;
    }
    case 'contact': {
      const name = g('cName');
      if (!name) return '';
      const lines = ['BEGIN:VCARD', 'VERSION:3.0', `FN:${escVcard(name)}`];
      const parts = name.split(/\s+/);
      const last = parts.length > 1 ? parts.pop()! : '';
      lines.push(`N:${escVcard(last)};${escVcard(parts.join(' '))};;;`);
      if (g('cOrg')) lines.push(`ORG:${escVcard(g('cOrg'))}`);
      if (g('cPhone')) lines.push(`TEL;TYPE=CELL:${g('cPhone')}`);
      if (g('cEmail')) lines.push(`EMAIL:${g('cEmail')}`);
      if (g('cUrl')) lines.push(`URL:${g('cUrl')}`);
      lines.push('END:VCARD');
      return lines.join('\n');
    }
    case 'whatsapp': {
      const digits = (f.waNumber ?? '').replace(/\D/g, '');
      if (!digits) return '';
      const msg = g('waMessage');
      return `https://wa.me/${digits}${msg ? '?text=' + encodeURIComponent(msg) : ''}`;
    }
    case 'geo': {
      const lat = g('geoLat');
      const lon = g('geoLon');
      if (!lat || !lon || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) return '';
      return `geo:${lat},${lon}`;
    }
    case 'event': {
      const title = g('evTitle');
      const hasStart = allDay ? icalDateOnly(f.evStart ?? '') : icalDate(f.evStart ?? '');
      if (!title && !hasStart) return '';
      const lines = ['BEGIN:VEVENT'];
      if (title) lines.push(`SUMMARY:${escICal(title)}`);
      if (allDay) {
        const ds = icalDateOnly(f.evStart ?? '');
        if (ds) {
          lines.push(`DTSTART;VALUE=DATE:${ds}`);
          lines.push(`DTEND;VALUE=DATE:${icalDateOnly(f.evEnd ?? '') || nextDay(ds)}`);
        }
      } else {
        const ds = icalDate(f.evStart ?? '');
        if (ds) {
          lines.push(`DTSTART:${ds}`);
          const de = icalDate(f.evEnd ?? '') || plusOneHour(f.evStart ?? '');
          if (de) lines.push(`DTEND:${de}`);
        }
      }
      if (g('evLocation')) lines.push(`LOCATION:${escICal(g('evLocation'))}`);
      if (g('evDesc')) lines.push(`DESCRIPTION:${escICal(g('evDesc'))}`);
      lines.push('END:VEVENT');
      return lines.join('\n');
    }
    default:
      return '';
  }
}

const EMPTY_HINT: Record<QrType, string> = {
  text: 'Enter some text or a URL to encode.',
  wifi: 'Enter your network name to make a Wi-Fi QR code.',
  email: 'Enter a recipient to make an email QR code.',
  phone: 'Enter a phone number.',
  sms: 'Enter a phone number to make an SMS QR code.',
  contact: 'Enter a name to make a contact (vCard) QR code.',
  whatsapp: 'Enter a phone number (with country code) for WhatsApp.',
  geo: 'Enter a latitude and longitude.',
  event: 'Enter a title or start time to make an event QR code.',
};

export default function BrowserQrCodeGenerator() {
  const [type, setType] = useState<QrType>('text');
  const [fields, setFields] = useState<Fields>(INITIAL_FIELDS);
  const [allDay, setAllDay] = useState(false);
  const [ecc, setEcc] = useState<QrEcc>('M');
  const [size, setSize] = useState('512');
  const [dark, setDark] = useState('#000000');
  const [light, setLight] = useState('#ffffff');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [encoderOk] = useState(() => verifyQrEncoder());

  const setField = (name: string, value: string) =>
    setFields((prev) => ({ ...prev, [name]: value }));

  // Deep-link support: /tools/qr-code-generator?type=wifi preselects that generator.
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('type');
    if (raw && TYPE_SET.has(raw as QrType)) setType(raw as QrType);
  }, []);

  const payload = useMemo(() => buildPayload(type, fields, allDay), [type, fields, allDay]);

  const result = useMemo(() => {
    if (!payload) {
      return { ok: false as const, error: EMPTY_HINT[type] };
    }
    try {
      const qr = encodeQr(payload, ecc);
      const pixels = Number.parseInt(size, 10) || 512;
      const svg = qrToSvg(qr, { size: pixels, margin: 4, dark, light });
      return { ok: true as const, qr, svg, pixels };
    } catch (error) {
      return {
        ok: false as const,
        error: error instanceof Error ? error.message : 'This content could not be encoded.',
      };
    }
  }, [payload, ecc, size, dark, light, type]);

  async function handleCopySvg() {
    if (!result.ok) return;
    try {
      await copyText(result.svg);
      setCopyMessage('QR code SVG copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  async function handleCopyText() {
    if (!result.ok) return;
    try {
      await copyText(payload);
      setCopyMessage('Encoded content copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  function handleDownloadPng() {
    if (!result.ok || typeof document === 'undefined') return;
    const pixels = result.pixels;
    const image = new Image();
    const svgBlob = new Blob([result.svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixels;
      canvas.height = pixels;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return;
      }
      ctx.drawImage(image, 0, 0, pixels, pixels);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const pngUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = pngUrl;
        anchor.download = `qr-${type}.png`;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
    };
    image.onerror = () => URL.revokeObjectURL(url);
    image.src = url;
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">QR code</span>
        <h2 className="tool-live-title">QR code generator</h2>
        <p className="tool-live-subtitle">
          Turn a link, Wi-Fi network, contact card, email, phone, SMS, WhatsApp, location, or
          calendar event into a scannable QR code. The encoder runs entirely in your browser with
          selectable error correction, custom colors, and PNG or SVG export.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>QR type</span>
              <select onChange={(e) => setType(e.target.value as QrType)} value={type}>
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {type === 'text' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Text or URL</span>
                <textarea
                  onChange={(e) => setField('text', e.target.value)}
                  rows={3}
                  value={fields.text ?? ''}
                />
              </label>
            ) : null}

            {type === 'wifi' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Network name (SSID)</span>
                  <input
                    onChange={(e) => setField('ssid', e.target.value)}
                    type="text"
                    value={fields.ssid ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Password</span>
                  <input
                    onChange={(e) => setField('pass', e.target.value)}
                    type="text"
                    value={fields.pass ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Security</span>
                  <select
                    onChange={(e) => setField('enc', e.target.value)}
                    value={fields.enc ?? 'WPA'}
                  >
                    <option value="WPA">WPA/WPA2</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">None</option>
                  </select>
                </label>
              </>
            ) : null}

            {type === 'email' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>To</span>
                  <input
                    onChange={(e) => setField('emailTo', e.target.value)}
                    type="email"
                    value={fields.emailTo ?? ''}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Subject</span>
                  <input
                    onChange={(e) => setField('emailSubject', e.target.value)}
                    type="text"
                    value={fields.emailSubject ?? ''}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Message</span>
                  <textarea
                    onChange={(e) => setField('emailBody', e.target.value)}
                    rows={2}
                    value={fields.emailBody ?? ''}
                  />
                </label>
              </>
            ) : null}

            {type === 'phone' ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Phone number</span>
                <input
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="+1 555 123 4567"
                  type="tel"
                  value={fields.phone ?? ''}
                />
              </label>
            ) : null}

            {type === 'sms' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Phone number</span>
                  <input
                    onChange={(e) => setField('smsNumber', e.target.value)}
                    type="tel"
                    value={fields.smsNumber ?? ''}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Message</span>
                  <textarea
                    onChange={(e) => setField('smsMessage', e.target.value)}
                    rows={2}
                    value={fields.smsMessage ?? ''}
                  />
                </label>
              </>
            ) : null}

            {type === 'contact' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Full name</span>
                  <input
                    onChange={(e) => setField('cName', e.target.value)}
                    type="text"
                    value={fields.cName ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Phone</span>
                  <input
                    onChange={(e) => setField('cPhone', e.target.value)}
                    type="tel"
                    value={fields.cPhone ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Email</span>
                  <input
                    onChange={(e) => setField('cEmail', e.target.value)}
                    type="email"
                    value={fields.cEmail ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Organization</span>
                  <input
                    onChange={(e) => setField('cOrg', e.target.value)}
                    type="text"
                    value={fields.cOrg ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Website</span>
                  <input
                    onChange={(e) => setField('cUrl', e.target.value)}
                    type="text"
                    value={fields.cUrl ?? ''}
                  />
                </label>
              </>
            ) : null}

            {type === 'whatsapp' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Phone number (with country code)</span>
                  <input
                    onChange={(e) => setField('waNumber', e.target.value)}
                    placeholder="15551234567"
                    type="tel"
                    value={fields.waNumber ?? ''}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Message</span>
                  <textarea
                    onChange={(e) => setField('waMessage', e.target.value)}
                    rows={2}
                    value={fields.waMessage ?? ''}
                  />
                </label>
              </>
            ) : null}

            {type === 'geo' ? (
              <>
                <label className="tool-live-field">
                  <span>Latitude</span>
                  <input
                    inputMode="decimal"
                    onChange={(e) => setField('geoLat', e.target.value)}
                    placeholder="40.71872"
                    type="text"
                    value={fields.geoLat ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Longitude</span>
                  <input
                    inputMode="decimal"
                    onChange={(e) => setField('geoLon', e.target.value)}
                    placeholder="-73.98905"
                    type="text"
                    value={fields.geoLon ?? ''}
                  />
                </label>
              </>
            ) : null}

            {type === 'event' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Title</span>
                  <input
                    onChange={(e) => setField('evTitle', e.target.value)}
                    type="text"
                    value={fields.evTitle ?? ''}
                  />
                </label>
                <label
                  className="tool-live-field tool-live-field-full"
                  style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}
                >
                  <input
                    checked={allDay}
                    onChange={(e) => setAllDay(e.target.checked)}
                    type="checkbox"
                  />
                  <span>All-day event</span>
                </label>
                <label className="tool-live-field">
                  <span>Starts</span>
                  <input
                    onChange={(e) => setField('evStart', e.target.value)}
                    type={allDay ? 'date' : 'datetime-local'}
                    value={fields.evStart ?? ''}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Ends</span>
                  <input
                    onChange={(e) => setField('evEnd', e.target.value)}
                    type={allDay ? 'date' : 'datetime-local'}
                    value={fields.evEnd ?? ''}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Location</span>
                  <input
                    onChange={(e) => setField('evLocation', e.target.value)}
                    type="text"
                    value={fields.evLocation ?? ''}
                  />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Notes</span>
                  <textarea
                    onChange={(e) => setField('evDesc', e.target.value)}
                    rows={2}
                    value={fields.evDesc ?? ''}
                  />
                </label>
              </>
            ) : null}

            <label className="tool-live-field">
              <span>Error correction</span>
              <select onChange={(e) => setEcc(e.target.value as QrEcc)} value={ecc}>
                {QR_ECC_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Export size</span>
              <select onChange={(e) => setSize(e.target.value)} value={size}>
                {SIZE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Foreground</span>
              <input onChange={(e) => setDark(e.target.value)} type="color" value={dark} />
            </label>
            <label className="tool-live-field">
              <span>Background</span>
              <input onChange={(e) => setLight(e.target.value)} type="color" value={light} />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!result.ok}
              onClick={handleDownloadPng}
              type="button"
            >
              Download PNG
            </button>
            <button
              className="button button-secondary"
              disabled={!result.ok}
              onClick={handleCopySvg}
              type="button"
            >
              Copy SVG
            </button>
            <button
              className="button button-secondary"
              disabled={!result.ok}
              onClick={handleCopyText}
              type="button"
            >
              Copy content
            </button>
          </div>

          {result.ok ? (
            <p className="tool-live-panel-note">
              Version {result.qr.version} &middot; {result.qr.size}&times;{result.qr.size} modules
              &middot; mask {result.qr.mask}
            </p>
          ) : null}
          {result.ok === false ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.error}
            </p>
          ) : null}
          {!encoderOk ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              The QR encoder self-test failed in this browser.
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Live preview
          </span>
          <div
            style={{
              background: light,
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '220px',
              overflow: 'hidden',
            }}
          >
            {result.ok ? (
              <span
                style={{ width: '220px', height: '220px', display: 'block' }}
                dangerouslySetInnerHTML={{
                  __html: result.svg
                    .replace(/width="\d+"/, 'width="220"')
                    .replace(/height="\d+"/, 'height="220"'),
                }}
              />
            ) : (
              <span style={{ color: '#9ca3af', fontSize: '13px' }}>No QR code to display.</span>
            )}
          </div>
          {result.ok ? (
            <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
              <span>SVG markup</span>
              <textarea readOnly rows={6} value={result.svg} />
            </label>
          ) : null}
          <p className="tool-live-panel-note">
            Encoded locally in your browser. Free, in your browser, nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
